import { memo, useMemo } from "react";
import type { MessageContent } from "duck/ui/chat/serverSentEvents/types";
import { useTranslation } from "react-i18next";
import ReactMarkdown, { Components } from "react-markdown";
import rehypeRaw from "rehype-raw";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import remarkGfm from "remark-gfm";

import Typography from "features/components/Typography";
import Link from "features/ui/Link";

import MarkdownAccordion from "./MarkdownAccordion";
import MarkdownImage from "./MarkdownImage";
import ViaductLink from "./ViaductLink";
import { ViaductLinkRegex } from "./ViaductLink/constants";

interface Props {
  content: MessageContent;
}

// We allow raw HTML inside messages (rehype-raw) but keep the sanitize allowlist
// as tight as possible: only <details>/<summary> are added on top of the safe
// defaults so AI/server content can render collapsibles. Widening this (extra
// tags, attributes, or a "*" wildcard) would reintroduce XSS — see the regression
// test guarding it.

export const markdownSanitizeSchema = {
  ...defaultSchema,
  tagNames: [...(defaultSchema.tagNames || []), "details", "summary"],
  attributes: {
    ...defaultSchema.attributes,
    details: ["open"],
  },
};

export const MarkdownContent = memo(({ content }: Props) => {
  const { t } = useTranslation();
  const components: Components = useMemo(
    () => ({
      a: ({ node, ...props }) => {
        const allProps: Record<string, any> = { ...props };

        // Handle custom protocol links (e.g., viaduct+[action]://duck.link...)
        if (typeof content === "string") {
          // 'node' contains positions of a link in the original markdown content
          const linkSubstring = content.substring(
            node?.position?.start.offset ?? 0,
            node?.position?.end.offset ?? 0
          );

          if (linkSubstring.match(ViaductLinkRegex)) {
            return <ViaductLink content={linkSubstring} />;
          }
        }

        // External links open in a new tab.
        // Internal links open in the same tab. Naive of course.
        if (props.href?.startsWith("http")) {
          allProps.target = "_blank";
        }

        return <Link {...allProps} to={props.href ?? "#"} />;
      },
      img: ({ node, ...props }) => {
        const { src, alt, ...rest } = props;
        if (!src) {
          return null;
        }

        return (
          <MarkdownImage
            src={String(props.src)}
            alt={props.alt ?? t("common:image")}
            {...rest}
          />
        );
      },
      ul: ({ node, ...props }) => (
        <ul style={{ listStyleType: "disc", paddingLeft: "20px" }} {...props} />
      ),
      ol: ({ node, ...props }) => (
        <ol
          style={{ listStyleType: "decimal", paddingLeft: "20px" }}
          {...props}
        />
      ),
      li: ({ node, ...props }) => (
        <li className="first:mt-3 mb-3 leading-6" {...props} />
      ),
      table: ({ node, ...props }) => (
        <div className="my-3 overflow-x-auto rounded border border-gray-100">
          <table
            className="w-full table-auto border-collapse text-xs [&_tbody_tr:hover]:bg-gray-50 [&_tbody_tr:last-child_td]:border-b-0"
            {...props}
          />
        </div>
      ),
      thead: ({ node, ...props }) => (
        <thead className="bg-gray-50" {...props} />
      ),
      tbody: ({ node, ...props }) => <tbody {...props} />,
      tr: ({ node, ...props }) => <tr {...props} />,
      th: ({ node, ...props }) => (
        <th
          className="border-b border-gray-200 px-3 py-2 text-left font-semibold text-gray-700"
          {...props}
        />
      ),
      td: ({ node, ...props }) => (
        <td
          className="border-b border-gray-100 px-3 py-2 text-gray-800"
          {...props}
        />
      ),
      h1: ({ node, ...props }) => (
        <Typography variant="h4" gutterBottom {...props} className="mt-8!" />
      ),
      h2: ({ node, ...props }) => (
        <Typography variant="h4" gutterBottom {...props} className="mt-6!" />
      ),
      h3: ({ node, ...props }) => (
        <Typography variant="h4" gutterBottom {...props} className="mt-4!" />
      ),
      h4: ({ node, ...props }) => (
        <Typography variant="h4" gutterBottom {...props} className="mt-4!" />
      ),
      h5: ({ node, ...props }) => (
        <Typography variant="h4" gutterBottom {...props} className="mt-4!" />
      ),
      h6: ({ node, ...props }) => (
        <Typography variant="h4" gutterBottom {...props} className="mt-4!" />
      ),
      details: ({ node, children, ...props }) => (
        <MarkdownAccordion open={Boolean(props.open)}>
          {children}
        </MarkdownAccordion>
      ),
      // Keep paragraphs as paragraphs - let ReactMarkdown handle structure properly
      p: ({ node, ...props }) => <p className="mb-2 last:mb-0" {...props} />,
      // Code blocks and inline code
      code: ({ node, ...props }: any) => {
        const { inline, className, children, ...restProps } = props;
        const {
          position: { start, end },
        } = node;
        if (inline || start.line === end.line) {
          return (
            <code
              className="bg-gray-50 px-1 py-0.5 rounded text-sm font-mono text-gray-800"
              {...restProps}
            >
              {children}
            </code>
          );
        }

        return (
          <code
            className="block bg-gray-50 p-4 rounded text-sm font-mono overflow-x-auto whitespace-pre text-gray-800"
            {...restProps}
          >
            {children}
          </code>
        );
      },
      pre: ({ node, children, ...props }) => (
        <pre className="bg-gray-50 rounded my-3 overflow-x-auto" {...props}>
          {children}
        </pre>
      ),
      hr: ({ node, ...props }) => (
        <hr className="border-t border-gray-100 my-6" {...props} />
      ),
    }),
    [content, t]
  );

  return (
    <ReactMarkdown
      remarkPlugins={[[remarkGfm, { singleTilde: false }]]}
      rehypePlugins={[rehypeRaw, [rehypeSanitize, markdownSanitizeSchema]]}
      components={components}
    >
      {typeof content === "string" ? content : JSON.stringify(content)}
    </ReactMarkdown>
  );
});
